import { useCallback, useEffect, useRef, useState } from "react";
import { buildVisitPayload, currentTimeOfDay, isWithinJourney, parseCoordinates } from "./cityVisitsModel";

const EMPTY_DRAFT = { locationId: "", date: "", time: "", areasVisited: "", latitude: "", longitude: "", remarks: "" };

// Why the browser could not give a position, and what to do about it.
// (GeolocationPositionError codes: 1 denied, 2 unavailable, 3 timeout.)
export function geolocationProblem(error) {
  if (!error) return "Could not read your location.";
  if (error.code === 1) return "Location permission was denied. Allow location access for this site in your browser settings, then press “Use my location” again — or enter the coordinates.";
  if (error.code === 2) return "Your device could not determine its location right now (no GPS / network fix). Move to open sky or try again — or enter the coordinates.";
  if (error.code === 3) return "Getting your location took too long. Try again — or enter the coordinates.";
  return `Could not read your location: ${error.message || "unknown error"}.`;
}

// The state and rules of the "Add City Visit" form, shared by the City Visits
// screen (CityVisitFormCard) and the journey page's "Visit Now" dialog
// (JourneyVisitNowModal), so both behave identically. The visit is only cleared
// from the form once the server has recorded it, and a refusal (wrong state,
// duplicate, ...) stays on screen with everything still filled in.
//
// Every field except Remarks is REQUIRED. When the form opens (and again after
// each saved visit) Time is pre-filled with the current time and the device's
// position is asked for ONCE (no tracking) to fill Latitude/Longitude; "Use my
// location" asks again. Everything stays visible and editable, and is only
// sent when the visit is submitted.
//
// `initialLocationId` ("Visit Now"): the stop is selected once the journey's
// cities have loaded, and Areas Visited starts with its planned areas (still
// editable). The server re-checks the journey and the stop when the visit is saved.
export default function useCityVisitForm({ journeys, journeyId, onJourneyChange, cities, onSubmit, initialLocationId = "" }) {
  const [draft, setDraft] = useState(() => ({ ...EMPTY_DRAFT, time: currentTimeOfDay() }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // A second click while the first submission is still in flight is ignored
  // (state alone updates too late to stop a real double click).
  const submitting = useRef(false);
  const prefilled = useRef(false);
  const [locating, setLocating] = useState(false);
  const [capture, setCapture] = useState(null); // { accuracy } of the last captured position
  // A position (or its failure) arriving after the form has closed is dropped.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const change = (key) => (event) => {
    setDraft((previous) => ({ ...previous, [key]: event.target.value }));
    if (key === "latitude" || key === "longitude") setCapture(null); // edited by hand
    setError("");
  };

  const captureLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("This browser cannot share its location. Enter the latitude and longitude instead.");
      return;
    }
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setError("Location is only available on a secure (https) connection. Enter the latitude and longitude instead.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!mounted.current) return;
        setDraft((previous) => ({
          ...previous,
          latitude: Number(position.coords.latitude).toFixed(6),
          longitude: Number(position.coords.longitude).toFixed(6),
        }));
        setCapture({ accuracy: Number.isFinite(position.coords.accuracy) ? Math.round(position.coords.accuracy) : null });
        setLocating(false);
      },
      (err) => {
        if (!mounted.current) return;
        setLocating(false);
        setError(geolocationProblem(err));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  // Auto-fill the location as soon as the form opens.
  useEffect(() => {
    captureLocation();
  }, [captureLocation]);

  // "Visit Now": select the stop once the journey's cities have loaded (only a
  // stop that can still be visited), with its planned areas as a starting point.
  useEffect(() => {
    if (prefilled.current || !initialLocationId) return;
    const stop = cities.find((city) => city.id === String(initialLocationId));
    if (!stop || stop.status) return;
    prefilled.current = true;
    setDraft((previous) => ({ ...previous, locationId: stop.id, areasVisited: previous.areasVisited || (stop.areas || []).join(", ") }));
  }, [cities, initialLocationId]);

  // The chosen journey's dates: the calendar only offers days between them.
  const selectedJourney = journeys.find((journey) => journey.id === String(journeyId));
  const range = { startDate: selectedJourney?.startDate || "", endDate: selectedJourney?.endDate || "" };

  const coordinates = parseCoordinates(draft.latitude, draft.longitude);
  const requiredFilled = Boolean(
    journeyId && draft.locationId && draft.date && draft.time && draft.latitude.trim() && draft.longitude.trim()
  );

  const selectedCity = cities.find((city) => city.id === draft.locationId) || null;

  const changeJourney = (nextJourneyId) => {
    const next = journeys.find((journey) => journey.id === nextJourneyId);
    onJourneyChange?.(nextJourneyId);
    // A date outside the newly chosen journey's dates is cleared.
    setDraft((previous) => ({
      ...previous,
      locationId: "",
      date: previous.date && next && !isWithinJourney(previous.date, { startDate: next.startDate, endDate: next.endDate }) ? "" : previous.date,
    }));
    setError("");
  };

  const handleSubmit = async () => {
    if (submitting.current) return;
    const built = buildVisitPayload(draft, journeyId, range);
    if (built.error) {
      setError(built.error);
      if (built.field) document.getElementById(`cv-${built.field}`)?.focus();
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError("");
    const result = await onSubmit(built.payload);
    submitting.current = false;
    if (result.ok && result.leaving) return; // the form is closing (back to the journey, or the dialog)
    setSaving(false);
    if (result.ok) {
      // Ready for the next visit: fresh time and position, like a newly opened form.
      setDraft({ ...EMPTY_DRAFT, time: currentTimeOfDay() });
      setCapture(null);
      captureLocation();
    } else setError(result.message);
  };

  return { draft, change, changeJourney, error, saving, locating, capture, captureLocation, range, coordinates, requiredFilled, selectedCity, handleSubmit };
}
